import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Phone,
  Mail,
  MapPin,
  Search,
  Inbox,
  TrendingUp,
  Clock,
  X,
  LayoutList,
  BarChart3,
  Wallet,
} from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { adminApi } from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import AnalyticsTab from "@/admin/AnalyticsTab";
import BillingTab from "@/admin/BillingTab";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TABS = [
  { id: "leads", label: "Leads", icon: LayoutList },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "billing", label: "Billing", icon: Wallet },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isClient = user?.role === "client";

  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [l, a, b] = await Promise.all([
        adminApi.get("/leads"),
        adminApi.get("/analytics"),
        adminApi.get("/billing/summary"),
      ]);
      setLeads(l.data);
      setAnalytics(a.data);
      setBilling(b.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Handle Stripe return
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setTab("billing");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await adminApi.get(`/billing/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          toast.success("Payment successful — balance settled!");
          setParams({}, { replace: true });
          loadAll();
          return;
        }
        if (res.data.status === "expired" || attempts >= 5) {
          toast.error("Payment not completed.");
          setParams({}, { replace: true });
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setParams({}, { replace: true });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.phone.toLowerCase().includes(q) ||
        l.service.toLowerCase().includes(q) ||
        (l.address || "").toLowerCase().includes(q)
    );
  }, [leads, query]);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-[#374151] bg-[#0A0A0A] sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl py-4 flex items-center justify-between">
          <LogoLockup inverted />
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-2 text-sm text-white/50">
              <span className="text-xs uppercase tracking-wider px-2 py-0.5 border border-[#374151] text-[#D86C1E]">
                {isClient ? "Client" : "Agency"}
              </span>
              {user?.email}
            </span>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="inline-flex items-center gap-2 border border-[#374151] hover:border-[#D86C1E] text-white text-sm font-heading font-bold uppercase tracking-wide px-4 py-2 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 max-w-7xl py-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-white">
            {isClient ? "Your Roofing Dashboard" : "Agency Dashboard"}
          </h1>
          <p className="text-white/50 mt-2">
            {isClient
              ? "Your leads, performance, and billing — all in one place."
              : "Track lead performance and billing for Schuyler."}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#374151] mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 font-heading font-bold uppercase tracking-wide text-sm transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-[#D86C1E] text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-20 text-center text-white/40 animate-pulse">Loading…</div>
        ) : tab === "leads" ? (
          <LeadsPanel
            leads={leads}
            filtered={filtered}
            query={query}
            setQuery={setQuery}
            onSelect={setSelected}
          />
        ) : tab === "analytics" ? (
          <AnalyticsTab data={analytics} />
        ) : (
          <BillingTab data={billing} isClient={isClient} onPaid={loadAll} />
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div
            data-testid="lead-detail"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#1A1A1A] border-l border-[#374151] h-full overflow-y-auto p-8"
          >
            <button
              data-testid="close-detail-btn"
              onClick={() => setSelected(null)}
              className="absolute top-6 right-6 text-white/50 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#D86C1E]">Lead Details</p>
            <h2 className="font-heading text-2xl font-black text-white mt-2">{selected.name}</h2>
            <p className="text-sm text-white/40 mt-1">{fmtDate(selected.created_at)}</p>

            <div className="mt-8 space-y-5">
              <a href={`tel:${selected.phone}`} className="flex items-center gap-4 group">
                <div className="flex h-11 w-11 items-center justify-center bg-[#D86C1E]">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Phone</div>
                  <div className="font-heading font-bold text-white group-hover:text-[#D86C1E]">{selected.phone}</div>
                </div>
              </a>
              <a href={`mailto:${selected.email}`} className="flex items-center gap-4 group">
                <div className="flex h-11 w-11 items-center justify-center bg-[#D86C1E]">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Email</div>
                  <div className="font-heading font-bold text-white group-hover:text-[#D86C1E] break-all">
                    {selected.email}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center bg-white/10">
                  <MapPin className="h-5 w-5 text-[#D86C1E]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Service Area</div>
                  <div className="font-heading font-bold text-white">{selected.address || "—"}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 border-t border-[#374151] pt-6">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Service Requested</div>
              <div className="inline-block bg-[#D86C1E]/15 text-[#D86C1E] font-heading font-bold text-sm px-3 py-1.5 mb-6">
                {selected.service}
              </div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Source</div>
              <div className="text-sm text-white/70 mb-6">{selected.source || "Agency (Website)"}</div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Message</div>
              <p className="text-sm text-white/70 leading-relaxed">
                {selected.message || "No message provided."}
              </p>
            </div>

            <a
              href={`tel:${selected.phone}`}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#D86C1E] hover:bg-[#B0551A] text-white font-heading font-bold uppercase tracking-wide px-6 py-3 transition-colors"
            >
              <Phone className="h-4 w-4" /> Call {selected.name.split(" ")[0]}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadsPanel({ leads, filtered, query, setQuery, onSelect }) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Stat icon={Inbox} value={leads.length} label="Total Leads" />
        <Stat
          icon={TrendingUp}
          value={leads.filter((l) => new Date(l.created_at) >= new Date(Date.now() - 7 * 864e5)).length}
          label="Last 7 Days"
        />
        <Stat icon={Clock} value={leads.filter((l) => l.status === "new").length} label="New / Unactioned" />
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <input
          data-testid="lead-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search leads…"
          className="w-full bg-[#1A1A1A] border border-[#374151] text-white placeholder:text-white/40 pl-10 pr-4 py-3 focus:outline-none focus:border-[#D86C1E] focus:ring-2 focus:ring-[#D86C1E]/40 transition-all"
        />
      </div>

      <div data-testid="leads-table" className="border border-[#374151] overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 bg-[#141414] px-6 py-3 text-xs uppercase tracking-wider text-white/40 font-bold">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Service</div>
          <div className="col-span-2">Area</div>
          <div className="col-span-2 text-right">Received</div>
        </div>
        {filtered.length === 0 ? (
          <div data-testid="no-leads" className="px-6 py-16 text-center text-white/40">
            {leads.length === 0 ? "No leads yet." : "No leads match your search."}
          </div>
        ) : (
          filtered.map((l) => (
            <button
              key={l.id}
              data-testid={`lead-row-${l.id}`}
              onClick={() => onSelect(l)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-t border-[#374151] hover:bg-[#141414] transition-colors items-center"
            >
              <div className="col-span-3 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[#D86C1E] shrink-0" />
                <span className="font-heading font-bold text-white">{l.name}</span>
              </div>
              <div className="col-span-3 text-sm text-white/60">
                <div>{l.phone}</div>
                <div className="truncate">{l.email}</div>
              </div>
              <div className="col-span-2 text-sm text-white/80">{l.service}</div>
              <div className="col-span-2 text-sm text-white/60">{l.address || "—"}</div>
              <div className="col-span-2 text-sm text-white/40 md:text-right">{fmtDate(l.created_at)}</div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-[#1A1A1A] border border-[#374151] p-6">
      <Icon className="h-5 w-5 text-[#D86C1E]" />
      <div className="font-heading text-4xl font-black text-white mt-4">{value}</div>
      <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{label}</div>
    </div>
  );
}
